import React, { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  MoreHorizontal, Link2, MessageCircle, Linkedin, 
  Pencil, Trash2, Loader2, Zap, Bell, Mail, Smartphone, UserPlus, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import FavoriteButton from './FavoriteButton';
import { usePlanGate, planGateFromResponse } from '../lib/planGate';
import PlanLimitDialog from './dialogs/PlanLimitDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EntityCardActions - Standardized action buttons for entity cards
 * Provides a unified dropdown menu for sharing, editing, and deleting items
 * Used across News, Tenders, Projects, and Companies pages
 */
// WO-33 — "Set Alert" on a project or company card created a TENDER alert with
// every filter empty (the entity it named was dropped by the server), so it
// emailed every new tender: that is how "Company: ACWA Power" sent ~200 emails
// on 21/09/2026. There is no project/company watch behind it, so the item is
// withdrawn until one exists; the server now refuses alerts without criteria.
const WATCH_SUPPORTED = false;

export default function EntityCardActions({
  // Item info
  itemId,
  itemType, // 'news', 'tender', 'project', 'company', 'opportunity'
  itemTitle,
  itemUrl,
  
  // Share options
  shareUrl = null, // Custom share URL (for private items with share tokens)
  
  // Permissions
  isAdmin = false,
  canEdit = false,
  canLink = false,
  canExtractLeads = false,
  canWatch = false, // Enable "Watch" / alert functionality
  canSaveAsLead = false, // Enable "Extract as Lead" (projects/companies)
  saveAsLeadLabel, // WO-33 — optional label, e.g. "Add to CRM" on tenders
  savedAsLeadLabel, // WO-33 — optional label once saved, e.g. "In your CRM"
  actionsOnbTarget, // WO-33 — data-onb marker on the "…" button, for the tour
  
  // Callbacks
  onEdit,
  onDelete,
  onLink,
  onExtractLeads,
  onSaveAsLead,
  
  // States
  isDeleting = false,
  isExtractingLeads = false,
  isSavingLead = false,
  savedAsLead = false,
  
  // Favorite props
  isFavorite = false,
  onFavoriteToggle,
  token,
  
  // User info for alerts
  userPlanId,
  
  // Language
  language = 'en',
  isRTL = false,
  
  // Primary action
  primaryAction,
  primaryLabel,
  primaryIcon: PrimaryIcon,
  
  // Additional content (rendered before admin actions)
  children
}) {
  // Watch/Alert dialog state
  const [showWatchDialog, setShowWatchDialog] = useState(false);
  const [watchAlertName, setWatchAlertName] = useState('');
  const [watchFrequency, setWatchFrequency] = useState('realtime');
  const [watchChannels, setWatchChannels] = useState(['in_app', 'email']);
  const [savingWatch, setSavingWatch] = useState(false);
  const planGate = usePlanGate();
  // Build the share URL
  const getShareUrl = () => {
    if (shareUrl) return shareUrl;
    return `${window.location.origin}/dashboard/${itemType}s/${itemId}`;
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    const url = getShareUrl();
    await navigator.clipboard.writeText(url);
    toast.success(language === 'ar' ? 'تم نسخ الرابط!' : 'Link copied!');
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };

  const handleLink = (e) => {
    e.stopPropagation();
    if (onLink) onLink();
  };

  const handleExtractLeads = (e) => {
    e.stopPropagation();
    if (onExtractLeads) onExtractLeads();
  };

  const handleSaveAsLead = (e) => {
    e.stopPropagation();
    if (onSaveAsLead) onSaveAsLead();
  };

  const handleOpenWatchDialog = (e) => {
    e.stopPropagation();
    // Check if user has paid plan
    if (userPlanId === 'free') {
      toast.error(language === 'ar' 
        ? 'التنبيهات غير متاحة في الخطة المجانية. يرجى الترقية.'
        : 'Alerts are not available on the Free plan. Please upgrade.');
      return;
    }
    
    // Set default name based on item
    const entityTypeNames = {
      project: language === 'ar' ? 'مشروع' : 'Project',
      company: language === 'ar' ? 'شركة' : 'Company',
      opportunity: language === 'ar' ? 'فرصة' : 'Opportunity'
    };
    setWatchAlertName(`${entityTypeNames[itemType] || itemType}: ${itemTitle?.substring(0, 50) || 'Unknown'}`);
    setShowWatchDialog(true);
  };

  const handleCreateWatchAlert = async () => {
    if (!watchAlertName.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم التنبيه' : 'Please enter an alert name');
      return;
    }
    
    setSavingWatch(true);
    try {
      const alertData = {
        name: watchAlertName,
        filters: {
          entity_type: itemType,
          entity_id: itemId,
          entity_title: itemTitle,
          countries: [],
          industries: [],
          keywords: [],
          min_value: null,
          max_value: null,
          sources: []
        },
        frequency: watchFrequency,
        channels: watchChannels,
        is_active: true
      };
      
      const response = await fetch(`${API_URL}/api/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });
      
      if (!response.ok) {
        // Plan refusal → shared upgrade dialog (402 plan_gate; 403 legacy).
        const pg = await planGateFromResponse(response);
        if (pg) {
          setShowWatchDialog(false);
          planGate.show(pg);
          setSavingWatch(false);
          return;
        }
        throw new Error('Failed to create alert');
      }
      
      toast.success(language === 'ar' 
        ? 'تم إنشاء التنبيه! ستتلقى إشعارات عند أي تحديث.'
        : 'Alert created! You\'ll be notified of any updates.');
      
      setShowWatchDialog(false);
      setWatchAlertName('');
      setWatchFrequency('realtime');
      setWatchChannels(['in_app', 'email']);
    } catch (error) {
      console.error('Error creating watch alert:', error);
      toast.error(language === 'ar' ? 'فشل إنشاء التنبيه' : 'Failed to create alert');
    } finally {
      setSavingWatch(false);
    }
  };

  const toggleWatchChannel = (channel) => {
    setWatchChannels(prev => 
      prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]
    );
  };

  return (
    <div className={`flex items-center justify-between w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Left side - Favorite only */}
      <FavoriteButton
        itemType={itemType}
        itemId={itemId}
        isFavorite={isFavorite}
        onToggle={onFavoriteToggle}
        token={token}
        compact
      />
      
      {/* Right side - More actions and primary button */}
      <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* More actions dropdown */}
        <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm"
            className="h-8 px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
            data-testid={`more-actions-${itemId}`}
            data-onb={actionsOnbTarget}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="end">
          <div className="flex flex-col">
            {/* Copy Link */}
            <Button 
              variant="ghost" 
              size="sm"
              className="justify-start h-9 px-3 text-sm"
              onClick={handleCopyLink}
            >
              <Link2 className="w-4 h-4 mr-2 text-blue-500" />
              {language === 'ar' ? 'نسخ الرابط' : 'Copy Link'}
            </Button>
            
            {/* Additional content (e.g., Link to Project for news/tenders) */}
            {children}
            
            {/* Extract as Lead - projects/companies, available to all users */}
            {canSaveAsLead && onSaveAsLead && (
              <>
                <div className="h-px bg-border my-1" />
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="justify-start h-9 px-3 text-sm"
                  onClick={handleSaveAsLead}
                  disabled={isSavingLead || savedAsLead}
                  data-testid={`save-as-lead-${itemId}`}
                >
                  {isSavingLead ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : savedAsLead ? (
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2 text-emerald-500" />
                  )}
                  {savedAsLead
                    ? (savedAsLeadLabel || (language === 'ar' ? 'في CRM' : 'In your CRM'))
                    : (saveAsLeadLabel || (language === 'ar' ? 'استخراج كعميل محتمل' : 'Extract as Lead'))}
                </Button>
              </>
            )}
            
            {/* Watch / Set Alert - for projects, companies, opportunities */}
            {canWatch && WATCH_SUPPORTED && (
              <>
                <div className="h-px bg-border my-1" />
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="justify-start h-9 px-3 text-sm"
                  onClick={handleOpenWatchDialog}
                  data-testid={`watch-${itemType}-${itemId}`}
                >
                  <Bell className="w-4 h-4 mr-2 text-amber-500" />
                  {language === 'ar' ? 'تعيين تنبيه' : 'Set Alert'}
                </Button>
              </>
            )}
            
            {/* Manage Links - for items that support it */}
            {canLink && onLink && (
              <>
                <div className="h-px bg-border my-1" />
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="justify-start h-9 px-3 text-sm"
                  onClick={handleLink}
                  data-testid={`link-${itemType}-${itemId}`}
                >
                  <Link2 className="w-4 h-4 mr-2 text-purple-500" />
                  {language === 'ar' ? 'إدارة الروابط' : 'Manage Links'}
                </Button>
              </>
            )}
            
            {/* Admin actions */}
            {(canExtractLeads || canEdit || isAdmin) && (
              <div className="h-px bg-border my-1" />
            )}
            
            {/* Extract Leads - Admin only for news/tenders */}
            {canExtractLeads && onExtractLeads && (
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start h-9 px-3 text-sm"
                onClick={handleExtractLeads}
                disabled={isExtractingLeads}
                data-testid={`extract-leads-${itemId}`}
              >
                {isExtractingLeads ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2 text-amber-500" />
                )}
                {language === 'ar' ? 'استخراج الفرص' : 'Extract Leads'}
              </Button>
            )}
            
            {/* Edit */}
            {canEdit && onEdit && (
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start h-9 px-3 text-sm"
                onClick={handleEdit}
                data-testid={`edit-${itemType}-${itemId}`}
              >
                <Pencil className="w-4 h-4 mr-2 text-blue-500" />
                {language === 'ar' ? 'تعديل' : 'Edit'}
              </Button>
            )}
            
            {/* Delete - shown whenever the parent provides an onDelete handler
                (parent decides eligibility: admin OR record owner). BUG-U102 FIX */}
            {onDelete && (
              <Button 
                variant="ghost" 
                size="sm"
                className="justify-start h-9 px-3 text-sm text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid={`delete-${itemType}-${itemId}`}
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {language === 'ar' ? 'حذف' : 'Delete'}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Primary action button */}
      {primaryAction && (
        <Button 
          size="sm" 
          className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            primaryAction();
          }}
          data-testid={`primary-action-${itemId}`}
        >
          {primaryLabel}
          {PrimaryIcon && <PrimaryIcon className="w-3 h-3 ml-1" />}
        </Button>
      )}
      </div>
      
      {/* Watch/Alert Dialog */}
      <Dialog open={showWatchDialog} onOpenChange={setShowWatchDialog}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {language === 'ar' ? 'تعيين تنبيه' : 'Set Alert'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'سيتم إعلامك عند أي تحديث لهذا العنصر'
                : 'You\'ll be notified when this item is updated'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Item being watched */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                {language === 'ar' ? 'مراقبة' : 'Watching'}
              </p>
              <p className="font-medium text-sm line-clamp-2">{itemTitle}</p>
            </div>
            
            {/* Alert Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'اسم التنبيه' : 'Alert Name'}</Label>
              <Input
                value={watchAlertName}
                onChange={(e) => setWatchAlertName(e.target.value)}
                placeholder={language === 'ar' ? 'اسم التنبيه...' : 'Alert name...'}
              />
            </div>
            
            {/* Frequency */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'تكرار الإشعارات' : 'Notification Frequency'}</Label>
              <Select value={watchFrequency} onValueChange={setWatchFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtime">
                    {language === 'ar' ? 'فوري' : 'Real-time (Immediate)'}
                  </SelectItem>
                  <SelectItem value="daily">
                    {language === 'ar' ? 'ملخص يومي' : 'Daily Digest'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notification Channels */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'قنوات الإشعار' : 'Notification Channels'}</Label>
              <div className="flex flex-wrap gap-2">
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    watchChannels.includes('in_app') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleWatchChannel('in_app')}
                >
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'التطبيق' : 'In-App'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    watchChannels.includes('email') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleWatchChannel('email')}
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'البريد' : 'Email'}</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    watchChannels.includes('push') ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleWatchChannel('push')}
                >
                  <Smartphone className="w-4 h-4" />
                  <span className="text-sm">{language === 'ar' ? 'المتصفح' : 'Push'}</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWatchDialog(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateWatchAlert} 
              disabled={savingWatch || !watchAlertName.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {savingWatch ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إنشاء التنبيه' : 'Create Alert'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
